<template>
  <div>
    <!-- Breadcrumb -->
    <app-breadcrumb>
      <template #contentright>
        <b-button
          variant="outline-primary"
          :to="{ name: 'users-add' }"
        >
          <span class="text-nowrap">
            <feather-icon icon="PlusCircleIcon" /> {{ $t('Add New') }}
          </span>
        </b-button>
      </template>
    </app-breadcrumb>

    <!-- Filters -->
    <users-list-filters
      :role-filter.sync="roleFilter"
      :status-filter.sync="statusFilter"
      :online-filter.sync="onlineFilter"
      :ban-filter.sync="banFilter"
      :department-filter.sync="departmentFilter"
      :search-query.sync="searchQuery"
      :role-options="roleOptions"
      :status-options="statusOptions"
      :online-options="onlineOptions"
      :ban-options="banOptions"
      :department-options="departmentOptions"
    />

    <!-- Table Container Card -->
    <b-overlay
      :show="loading"
      spinner-variant="primary"
      spinner-type="grow"
      spinner-small
      rounded="sm"
    >
      <b-card
        no-body
        class="mb-0"
      >

        <div class="m-2">
          <!-- Table Top -->
          <b-row>
            <!-- Per Page -->
            <b-col
              cols="12"
              md="6"
              class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
            >
              <label>{{ $t('Show') }}</label>
              <v-select
                v-model="perPage"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="perPageOptions"
                :clearable="false"
                class="per-page-selector d-inline-block mx-50"
              />
              <label>{{ $t('entries') }}</label>
            </b-col>

          </b-row>
        </div>

        <b-table
          ref="refUserListTable"
          class="position-relative table-nowrap"
          :items="itemsData"
          responsive
          :fields="tableColumns"
          primary-key="id"
          :sort-by.sync="sortBy"
          show-empty
          empty-text="Không tìm thấy kết quả"
          :sort-desc.sync="isSortDirDesc"
          selectable
          select-mode="multi"
          @row-selected="onRowSelected"
        >

          <!-- A custom formatted header cell for field 'selected' -->
          <template #head(selected)="{ rowSelected }">
            <b-form-checkbox
              v-model="selectedAll"
              value="1"
              unchecked-value="0"
              :checked="rowSelected"
              @change="selectAllRows"
            />
          </template>

          <!-- Column: User -->
          <template #cell(selected)="data">
            <b-form-checkbox
              v-model="selected"
              :value="data.item.id"
              @change="selectRow(data.item.id, data.index)"
            />
          </template>
          <!-- Column: Info -->
          <template #cell(info)="data">
            <div
              v-if="data.item.department"
              class="d-block"
            >
              <span class="font-weight-bolder">Phòng/Ban:</span> {{ data.item.department.name }}<br>
              <span class="font-weight-bolder">Phone:</span> {{ data.item.phone }}<br>
              <span class="font-weight-bolder">Email:</span> {{ data.item.email }}
            </div>
          </template>

          <!-- Column: User -->
          <template #cell(user)="data">
            <b-media vertical-align="center">
              <template #aside>
                <b-avatar
                  size="36"
                  :src="defaultAvatar(data.item.avatar)"
                  :text="avatarText(data.item.username)"
                  class="mr-1 cursor-pointer badge-minimal"
                  variant="light-primary"
                  badge
                  :badge-variant="resolveUserOnlineVariant(data.item.online)"
                  :to="{ name: 'users-edit', params: { id: data.item.id } }"
                />
              </template>
              <b-link
                :to="{ name: 'users-edit', params: { id: data.item.id } }"
                class="font-weight-bold d-block text-nowrap"
              >
                {{ data.item.full_name }}

              </b-link>
              <div class="text-muted">
                {{ data.item.username }}
              </div>
            </b-media>
          </template>

          <!-- Column: Role -->
          <template #cell(role)="data">
            <span
              v-for="(roleName, roleIdx) in resolveUserRoleName(data.item.role_names)"
              :key="`role${roleIdx}`"
              class="font-weight-bold text-capitalize d-block"
            >{{ roleName }}</span>
          </template>
          <!-- Column: Role -->
          <template #cell(group)="data">
            <span
              v-for="(groupName, groupIdx) in resolveUserRoleName(data.item.group_names)"
              :key="`group${groupIdx}`"
              class="font-weight-bold text-capitalize d-block"
            >{{ groupName }}</span>
          </template>
          <!-- Column: Online -->
          <!-- <template #cell(online)="data">
            <b-badge
              pill
              :variant="`light-${resolveUserOnlineVariant(data.item.online)}`"
              class="text-capitalize"
            >
              {{ onlineOptions[data.item.online].label }}
            </b-badge>
          </template> -->

          <!-- Column: Status -->
          <template #cell(status)="data">
            <b-badge
              pill
              :variant="`light-${resolveUserStatusVariant(data.item.status)}`"
              class="text-capitalize"
            >
              {{ resolveUserStatusText(data.item.status) }}
            </b-badge>
          </template>

          <!-- Column: Banned -->
          <template #cell(banned)="data">
            <b-button
              v-b-tooltip
              :variant="`flat-${resolveUserBanVariant(data.item.banned)}`"
              class="btn-icon"
              :title="resolveUserBanText(data.item.banned)"
              @click="toggleBan(data.item, data.index)"
            >
              <feather-icon
                v-if="!data.item.banned"
                icon="LockIcon"
              />
              <feather-icon
                v-else
                icon="UnlockIcon"
              />
            </b-button>
          </template>

          <!-- Column: Actions -->
          <template
            #cell(actions)="row"
            class="text-right"
          >
            <b-button
              v-if="$can('user.update', 'User')"
              v-ripple.400="'rgba(40, 199, 111, 0.15)'"
              v-b-tooltip
              class="btn-icon ml-1"
              variant="outline-primary"
              :to="{ name: 'users-edit', params: { id: row.item.id } }"
              title="Sửa"
              size="sm"
            >
              <feather-icon icon="EditIcon" />
            </b-button>
            <b-button
              v-if="$can('user.destroy', 'User')"
              v-ripple.400="'rgba(40, 199, 111, 0.15)'"
              v-b-tooltip
              class="btn-icon ml-1"
              variant="outline-danger"
              title="Xóa"
              size="sm"
              @click="confirmDelete(row.item.id)"
            >
              <feather-icon icon="TrashIcon" />
            </b-button>
          </template>
        </b-table>
        <div class="mx-2 mb-2">
          <b-row>

            <b-col
              cols="12"
              sm="6"
              class="d-flex align-items-center justify-content-center justify-content-sm-start"
            >
              <span class="text-muted">{{ $t('show_to_entries', { from: dataMeta.from, to: dataMeta.to, of: dataMeta.of }) }}</span>
            </b-col>
            <!-- Pagination -->
            <b-col
              cols="12"
              sm="6"
              class="d-flex align-items-center justify-content-center justify-content-sm-end"
            >
              <b-pagination
                v-model="currentPage"
                :total-rows="totalEntries"
                :per-page="perPage"
                first-number
                last-number
                class="mb-0 mt-1 mt-sm-0"
                prev-class="prev-item"
                next-class="next-item"
              >
                <template #prev-text>
                  <feather-icon
                    icon="ChevronLeftIcon"
                    size="18"
                  />
                </template>
                <template #next-text>
                  <feather-icon
                    icon="ChevronRightIcon"
                    size="18"
                  />
                </template>
              </b-pagination>

            </b-col>

          </b-row>
        </div>
      </b-card>
    </b-overlay>
  </div>
</template>

<script>
import {
  BOverlay, BCard, BRow, BCol, BAvatar, BButton, BTable,
  BBadge, BPagination, BMedia, BLink, BFormCheckbox, VBTooltip,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import { ref } from '@vue/composition-api'
import _ from 'lodash';
import Ripple from 'vue-ripple-directive'
import { avatarText } from '@core/utils/filter'
import { showToast, defaultAvatar } from '@/@core/utils/utils'
import AppBreadcrumb from '@core/layouts/components/AppBreadcrumb.vue'
import UsersListFilters from './UsersListFilters.vue'
import useUsers from './useUsers'
import useDepartments from '../department/useDepartments'

export default {
  components: {
    AppBreadcrumb,
    UsersListFilters,
    BOverlay,
    BCard,
    BRow,
    BCol,
    BButton,
    BTable,
    BBadge,
    BPagination,
    BAvatar,
    BMedia,
    BLink,
    BFormCheckbox,
    vSelect,
  },
  directives: {
    'b-tooltip': VBTooltip,
    Ripple,
  },
  data() {
    return {
      isShowModal: false,
    }
  },
  setup() {
    const isAddNewUserSidebarActive = ref(false)

    const selected = ref([]);
    const selectedAll = ref(0);

    const {
      roleOptions,
      itemsData,
      loading,
      departmentOptions,
      fetchDepartmentTrees,
      fetchUsers,
      fetchRoles,
      tableColumns,
      perPage,
      currentPage,
      totalEntries,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refUserListTable,
      refetchData,

      // UI
      resolveUserRoleVariant,
      resolveUserRoleIcon,
      resolveUserStatusVariant,
      resolveUserStatusText,
      resolveUserRoleName,
      resolveUserOnlineVariant,
      resolveUserBanVariant,
      resolveUserBanText,

      // Extra Filters
      roleFilter,
      departmentFilter,
      statusFilter,
      onlineFilter,
      onlineOptions,
      banFilter,
      banOptions,

      statusOptions,
    } = useUsers()

    fetchRoles();
    fetchDepartmentTrees();
    fetchUsers();

    return {
      departmentOptions,
      statusOptions,
      roleOptions,
      itemsData,
      // Sidebar
      isAddNewUserSidebarActive,
      loading,
      fetchUsers,
      tableColumns,
      perPage,
      currentPage,
      totalEntries,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refUserListTable,
      refetchData,

      // Filter
      avatarText,

      // UI
      resolveUserRoleVariant,
      resolveUserRoleIcon,
      resolveUserStatusVariant,
      resolveUserStatusText,
      resolveUserRoleName,
      resolveUserOnlineVariant,
      resolveUserBanVariant,
      resolveUserBanText,

      // Extra Filters
      roleFilter,
      departmentFilter,
      statusFilter,
      onlineFilter,
      onlineOptions,
      banFilter,
      banOptions,
      defaultAvatar,
      selected,
      selectedAll,
    }
  },
  methods: {
    confirmDelete(id) {
      this.$swal({
        title: 'Bạn chắc muốn xóa?',
        text: 'Xóa dữ liệu sẽ ảnh hưởng đến các chức năng khác trong hệ thống!',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Có',
        cancelButtonText: 'Không',
        customClass: {
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-outline-danger ml-1',
        },
        buttonsStyling: false,
      }).then(result => {
        if (result.value) {
          this.$store.dispatch('users/deleteUser', { id }).then((res) => {
            if (res.status === 200) {
              showToast(res.data.message, { variant: 'success' });
              this.fetchUsers();
            } else {
              showToast(res.data.message);
            }
          })
        }
      })
    },
    onRowSelected(items) {
      // this.selected = items
      this.selected = [];
      items.forEach(item => {
        this.selected.push(item.id);
      });
    },
    selectAllRows() {
      if (this.selectedAll === '1') {
        this.$refs.refUserListTable.selectAllRows();
      } else {
        this.clearSelected();
      }
    },
    clearSelected() {
      this.selected = [];
      this.$refs.refUserListTable.clearSelected()
    },
    selectRow(id, idx) {
      // eslint-disable-next-line no-underscore-dangle
      const _idx = _.indexOf(this.selected, id);
      if (_idx >= 0) {
        this.$refs.refUserListTable.selectRow(idx);
      } else {
        // _.remove(this.selected, (_id) => _id === id);
        this.$refs.refUserListTable.unselectRow(idx);
      }
    },
    toggleBan(user) {
      this.loading = true;
      this.$store.dispatch('users/banUser', user.id).then((res) => {
        this.loading = false;
        if (res.status === 200) {
          showToast(res.data.message, { variant: 'success' });
          this.itemsData.forEach((item, k) => {
            if (item.id === user.id) {
              this.itemsData[k].banned = res.data.data.banned;
            }
          });
        } else {
          showToast(res.data.message);
        }
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.per-page-selector {
  width: 90px;
}
</style>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
</style>
